/**
 * DaVinci ConvertResponseToNewRequest Endpoint
 *
 * Transforms BA response into format suitable for next action (B, BQ, BC).
 *
 * This endpoint performs critical transformations:
 * - Unpacks packages - Converts ServicePackage into individual services
 * - Adds metadata - ServiceProvider IDs, Flags, PriceInfo, SubCode fields
 * - Adds top-level sections - Delivery and Payment objects
 * - Enriches Participants - Adds Age, Code, Passport, Address, EmergencyContact fields
 */

import { davinciClient } from './davinciClient';
import {
  ConvertResponseRequestSchema,
  ConvertResponseResponseSchema,
  type ConvertResponseRequest,
  type ConvertResponseResponse,
  type DaVinciLogin,
} from '@/types/schemas';

/**
 * Convert BA response to format suitable for next action
 *
 * @param request - Convert request with SessionId, ActivityId, Booking
 * @returns Converted response with transformed Booking object
 * @throws {DaVinciNetworkError} Network or timeout errors
 * @throws {DaVinciApiError} API errors
 * @throws {ValidationError} Invalid request/response structure
 *
 * @example
 * const response = await convertResponseToNewRequest({
 *   Login: {},
 *   SessionId: baResponse.SessionId,
 *   ActivityId: baResponse.ActivityId,
 *   Booking: baResponse.Booking
 * });
 */
export async function convertResponseToNewRequest(
  request: ConvertResponseRequest
): Promise<ConvertResponseResponse> {
  const response = await davinciClient.post<ConvertResponseRequest, ConvertResponseResponse>({
    endpoint: '/ConvertResponseToNewRequest',
    data: request,
    requestSchema: ConvertResponseRequestSchema,
    responseSchema: ConvertResponseResponseSchema,
  });

  return response;
}

/**
 * Convenience wrapper with login parameter
 *
 * @param sessionId - SessionId from BA response
 * @param activityId - ActivityId from BA response
 * @param booking - Booking object from BA response
 * @param login - Empty login object (auth handled by API Gateway)
 * @returns Converted response with transformed Booking object
 *
 * @example
 * const converted = await convertResponse(
 *   baResponse.SessionId,
 *   baResponse.ActivityId,
 *   baResponse.Booking,
 *   {}
 * );
 */
export async function convertResponse(
  sessionId: string,
  activityId: string,
  booking: Record<string, unknown>,
  login: DaVinciLogin
): Promise<ConvertResponseResponse> {
  return convertResponseToNewRequest({
    Login: login,
    SessionId: sessionId,
    ActivityId: activityId,
    Booking: booking,
  });
}
